import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Observer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorDialogData } from '../components/modals/error-dialog-data';
import { ErrorDialogComponent } from '../components/modals/error-dialog/error-dialog.component';
import { AccountService } from './account.service';
import { LanguageService } from './language.service';
import { Rule } from '../components/rollout/rule';
import { MsalService } from '@azure/msal-angular';

/** Contains Api endpoints */

const API = environment.api + "api/configtool/";
const EOL_API = environment.api + "api/production/";

const ROLE = API + "role/query";

/* data list api endpoints */
const CONFIGURATION_LIST = API + "V2/configuration/list";
const CONFIGURATION_DETAILS = API + "V2/configuration/view";
const COMPONENT_LIST = API + "V2/component/list";
const ADD_COMPONENT_VARIANT = API + "V2/component/add-variant";
const RECALLED_COMPONENT_LIST = API + "V2/component/recall-list";
const RELEASE_LIST = API + "V2/release/list";
const RELEASE_ELEMENT = API + "V2/release/details";
const FIRMWARE_DOWNLOAD = EOL_API + "V2/firmware-download";
const SOFTWARE_MONITOR_LIST = API + "V2/sw-monitor";
const SOFTWARE_BIKE_DETAILS = API + "V2/bike/software-bikes";
const BIKE_OVERVIEW_LIST = API + "V2/bike/overview";
const BIKE_DETAILS = API + "V2/bike/bike-details"
const BIKE_OVERVIEW_LIST_CSV = API + "V2/bike/overviewCsv";
const SOFTWARE_BIKES_LIST_CSV = API + "V2/bike/software-bikes-csv";
const APPROVALBIKE_LIST = API + "V2/approval-bike/list";
const UNASSIGNEDBIKE_LIST = API + "V2/bike/list-unassigned";

const RECALLED_BULK_UPLOAD = API + "V2/component/recall-batch";
const ADD_CONFIGURATION = API + "V2/configuration/add";
const EDIT_CONFIGURATION = API + "V2/configuration/update";
const START_ROLLOUT = API + "V2/release/start-rollout";
const ACTIVATE_ROLLOUT = API + "V2/release/activate";
const ADD_RELEASE = API + "V2/release/add";
const EDIT_RELEASE = API + "V2/release/update";
const ADD_APPROVALBIKE = API + "V2/approval-bike/add";

const DELETE_CONFIGURATION = API + "V2/configuration/delete";
const DELETE_RELEASE = API + "V2/release/delete";

/* option list api endpoints */
const BRAND_LIST = API + "options/brands";
/** Unit list api endpoint */
const BIKE_TYPE_LIST = API + "options/bike_type";
const OE_LIST = API + "V2/options/oes";
const COMPONENT_CLASS_LIST = API + "options/componentclass";

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  /* option list value storing */
  private brandOptionsList: { id: number, name: string, OEM_ID: number }[] = [];
  private bikeTypeOptionsList: { id: number, name: string }[] = [];
  private oeOptionsList: Record<string, { id: number, name: string }[]> = {};
  private componentClassOptionsList: { id: number, name: string }[] = [];
  public serverErrorDialog: boolean = false;

  constructor(private accountService: AccountService, private http: HttpClient,
    private matDialog: MatDialog, private languageService: LanguageService,
    private msalService: MsalService) {
  }

  /** Authorization header  */
  private getHeader() {
    const activeAccount = this.msalService.instance.getAccountByUsername(this.accountService.User.Email);
    const idToken = activeAccount?.idToken;

    if (idToken) {
      return {
        headers: new HttpHeaders({
          'idtoken': idToken
        })
      }
    }
    else {
      return {};
    }
  }

  private getTextHeader() {
    const activeAccount = this.msalService.instance.getAccountByUsername(this.accountService.User.Email);
    const idToken = activeAccount?.idToken;

    if (idToken) {
      return {
        headers: new HttpHeaders({
          'idtoken': idToken
        }),
        responseType: 'text' as const
      }
    }
    else {
      return { responseType: 'text' as const };
    }
  }

  private getBlobHeader() {
    const activeAccount = this.msalService.instance.getAccountByUsername(this.accountService.User.Email);
    const idToken = activeAccount?.idToken;

    if (idToken) {
      return {
        headers: new HttpHeaders({
          'idtoken': idToken
        }),
        responseType: 'blob' as const
      }
    }
    else {
      return { responseType: 'blob' as const };
    }
  }

  /*
   * option list api calls
   * previously called functions returns stored values
   */

  public getBrandOptionsList(): Observable<{ id: number, name: string, OEM_ID: number }[]> {
    return new Observable((observer: Observer<{ id: number, name: string, OEM_ID: number }[]>) => {
      if (this.brandOptionsList.length != 0) {
        observer.next(this.brandOptionsList);
        observer.complete();
      }
      else {
        this.http.get(BRAND_LIST, this.getHeader()).subscribe((resp: any) => {
          if (resp && resp.data) {
            this.brandOptionsList = resp.data;
            observer.next(this.brandOptionsList);
            observer.complete();
          }
          else {
            observer.next([]);
            observer.complete();
          }
        }, (error) => {
          observer.next([]);
          observer.complete();
        });
      }
    });
  }

  public getBikeTypeOptionsList(): Observable<{ id: number, name: string }[]> {
    return new Observable((observer: Observer<{ id: number, name: string }[]>) => {
      if (this.bikeTypeOptionsList.length != 0) {
        observer.next(this.bikeTypeOptionsList);
        observer.complete();
      }
      else {
        this.http.get(BIKE_TYPE_LIST, this.getHeader()).subscribe((resp: any) => {
          if (resp && resp.data) {
            this.bikeTypeOptionsList = resp.data;
            observer.next(this.bikeTypeOptionsList);
            observer.complete();
          }
          else {
            observer.next([]);
            observer.complete();
          }
        }, (error) => {
          observer.next([]);
          observer.complete();
        });
      }
    });
  }

  public getOEOptionsList(alwaysShowZf: number, showAllForZf: number): Observable<{ id: number, name: string }[]> {
    return new Observable((observer: Observer<{ id: number, name: string }[]>) => {
      // check if it is already downloaded
      if (this.oeOptionsList[alwaysShowZf + showAllForZf]) {
        observer.next(this.oeOptionsList[alwaysShowZf + showAllForZf]);
        observer.complete();
      }
      else {
        this.http.get(OE_LIST + `/${alwaysShowZf}/${showAllForZf}`, this.getHeader()).subscribe((resp: any) => {
          if (resp && resp.data) {
            this.oeOptionsList[alwaysShowZf + showAllForZf] = resp.data;
            observer.next(this.oeOptionsList[alwaysShowZf + showAllForZf]);
            observer.complete();
          }
          else {
            observer.next([]);
            observer.complete();
          }
        }, (error) => {
          observer.next([]);
          observer.complete();
        });
      }
    });
  }

  public getComponentClassOptionsList(): Observable<{ id: number, name: string }[]> {
    return new Observable((observer: Observer<{ id: number, name: string }[]>) => {
      if (this.componentClassOptionsList.length != 0) {
        observer.next(this.componentClassOptionsList);
        observer.complete();
      }
      else {
        this.http.get(COMPONENT_CLASS_LIST, this.getHeader()).subscribe((resp: any) => {
          if (resp && resp.data) {
            this.componentClassOptionsList = resp.data;
            observer.next(this.componentClassOptionsList);
            observer.complete();
          }
          else {
            observer.next([]);
            observer.complete();
          }
        }, (error) => {
          observer.next([]);
          observer.complete();
        });
      }
    });
  }

  public getUserRole() {
    return this.http.get(ROLE, this.getHeader());
  }

  /* get configurations backend call */
  public getConfigurationList() {
    return this.http.get(CONFIGURATION_LIST, this.getHeader());
  }

  public getConfigurationDetails(configId: number) {
    return this.http.get(`${CONFIGURATION_DETAILS}/${configId}`, this.getHeader());
  }

  public getComponentList() {
    return this.http.get(COMPONENT_LIST, this.getHeader());
  }

  public addComponentVariant(postData: { component_id: number, variant_name: string, oe: number }) {
    return this.http.post(ADD_COMPONENT_VARIANT, postData, this.getHeader())
  }

  public getRecalledComponentList(postData: {
    order: string, direction: string, page: number, recordsPerPage: number
  }) {
    return this.http.post(RECALLED_COMPONENT_LIST, postData, this.getHeader());
  }

  public getApprovalBikeList(releaseId: number) {
    let options: any = this.getHeader();
    options.params = { release_id: releaseId };

    return this.http.get(APPROVALBIKE_LIST, options);
  }

  public addApprovalBike(postData: { bike_id: string, approval: string }) {
    return this.http.post(ADD_APPROVALBIKE, postData, this.getHeader());
  }

  public recalledBulkUpload(serials: string[], recalled: string) {
    const postData = { serials: serials, recalled: recalled };
    return this.http.post(RECALLED_BULK_UPLOAD, postData, this.getHeader());
  };

  public getReleaseList() {
    return this.http.get(RELEASE_LIST, this.getHeader());
  }

  public getRelease(releaseId: number) {
    return this.http.get(`${RELEASE_ELEMENT}/${releaseId}`, this.getHeader());
  }

  public downloadFirmware(releaseId: string) {
    return this.http.post(FIRMWARE_DOWNLOAD, { releaseId: releaseId }, this.getBlobHeader());
  }

  public addRelease(releaseNotes: string, componentVariantId: number, releaseVersion: string, sha: string, file: File, filename: string) {
    const formData = new FormData();
    formData.append("release_notes", releaseNotes);
    formData.append("component_variant_id", componentVariantId.toString());
    formData.append("release_version", releaseVersion);
    formData.append("sha", sha);
    formData.append("file", file, filename);

    return this.http.post(ADD_RELEASE, formData, this.getHeader());
  }

  public editRelease(releaseId: string, releaseNotes: string, componentVariantId: number, releaseVersion: string) {
    const formData = new FormData();
    formData.append("id", releaseId);
    formData.append("release_notes", releaseNotes);
    formData.append("component_variant_id", componentVariantId.toString());
    formData.append("release_version", releaseVersion);

    return this.http.post(EDIT_RELEASE, formData, this.getHeader());
  }

  public changeReleaseStatus(releaseId: string, componentVariantId: number, releaseVersion: string, activationStatus: number, approvalStatus: number, rolloutStatus: number) {
    const formData = new FormData();
    formData.append("id", releaseId);
    formData.append("component_variant_id", componentVariantId.toString());
    formData.append("release_version", releaseVersion);
    formData.append("activation_status", activationStatus.toString());
    formData.append("approval_status", approvalStatus.toString());
    formData.append("rollout_status", rolloutStatus.toString());

    return this.http.post(EDIT_RELEASE, formData, this.getHeader());
  }

  public deleteRelease(id: number) {
    return this.http.post(DELETE_RELEASE, { id: id }, this.getHeader());
  }

  public addStartApprovalRollout(releaseId: number, bikesList: string[], approvalRules: Rule[], rejectionRules: Rule[]) {
    const rolloutData = bikesList.map((el) => { return { bike_id: el } });
    const approvalRulesFormatted = approvalRules.map((el) => {
      return { op_approval_rules_id: el.Metric, comparator: el.Comparator, days: el.Interval, threshold: el.Threshold }
    });
    const rejectionRulesFormatted = rejectionRules.map((el) => {
      return { op_rejection_rules_id: el.Metric, comparator: el.Comparator, days: el.Interval, threshold: el.Threshold }
    });
    const postData = { release_id: releaseId, rollout_data: rolloutData, rules_approval: [approvalRulesFormatted], rules_rejection: [rejectionRulesFormatted] };

    return this.http.post(START_ROLLOUT, postData, this.getHeader());
  }

  public addDeactivationRules(releaseId: number, deactivationRules: Rule[]) {
    const deactivationRulesFormatted = deactivationRules.map((el) => {
      return { op_deactivation_rules_id: el.Metric, comparator: el.Comparator, days: el.Interval, threshold: el.Threshold }
    });

    let deactivationReleaseIds;
    if (deactivationRules?.length > 0) {
      deactivationReleaseIds = deactivationRules.map((el) => {
        return { release_id: releaseId };
      });
    }
    else {
      deactivationReleaseIds = [{ release_id: releaseId }];
    }

    const postData = { deactivation_data: deactivationReleaseIds, rules_deactivation: [deactivationRulesFormatted] };
    return this.http.post(ACTIVATE_ROLLOUT, postData, this.getHeader());
  }

  public startRollout(releaseId: number, rolloutType: string) {
    const postData = { release_id: releaseId, used_by: rolloutType };
    return this.http.post(ACTIVATE_ROLLOUT, postData, this.getHeader());
  }

  public addConfiguration(postData: {
    config_name: string,
    oe: number,
    brand: number,
    comment?: string,
    bike_model_name: string,
    bike_type: number,
    component_data: { id: number }[],
    component_variants: { id: number }[],
    opt_component_variants: { id: number }[],
    wheel_circumference: number,
    light_reserved_area: number,
    display_orientation: number,
    boot_logo_id: number
  }) {
    return this.http.post(ADD_CONFIGURATION, postData, this.getHeader());
  }

  public editConfiguration(postData: {
    id: number,
    config_name: string,
    oe: number,
    brand: number,
    comment?: string,
    bike_model_name: string
    bike_type: number,
    component_data: { id: number }[]
  }) {
    return this.http.post(EDIT_CONFIGURATION, postData, this.getHeader());
  }

  public deleteConfiguration(id: number) {
    return this.http.post(`${DELETE_CONFIGURATION}/${id}`, null, this.getHeader());
  }

  public getSoftwareMonitorList(releaseId: number) {
    return this.http.get(`${SOFTWARE_MONITOR_LIST}/${releaseId}`, this.getHeader());
  }

  public getSoftwareBikeDetails(rolloutId: number, approvalRollout: number, valueType: string, recordsPerPage: number, page: number,
    searchByList: any, direction: string, order: string
  ) {
    let postData: any = {};

    if (approvalRollout == 1) {
      postData = {
        approval_rollout_id: rolloutId, value_type: valueType, recordsPerPage: recordsPerPage, page: page, type: "Approval Rollout",
        direction: direction, order: order
      };
    }
    else {
      postData = {
        rollout_id: rolloutId, value_type: valueType, recordsPerPage: recordsPerPage, page: page, type: "Rollout",
        direction: direction, order: order
      };
    }

    if (Object.keys(searchByList).length) {
      postData.search = searchByList
    }

    return this.http.post(SOFTWARE_BIKE_DETAILS, postData, this.getHeader());
  }

  public getBikeOverviewList(postData: { order: string, direction: string, page: number, recordsPerPage: number }) {
    return this.http.post(BIKE_OVERVIEW_LIST, postData, this.getHeader());
  }

  public getBikeDetails(bikeId: string) {
    return this.http.get(`${BIKE_DETAILS}/${bikeId}`, this.getHeader());
  }

  public getBikeOverviewListCSV() {
    return this.http.get(BIKE_OVERVIEW_LIST_CSV, this.getTextHeader());
  }

  public getSoftwareBikesListCSV(postData: { approval_rollout_id?: number, rollout_id?: number }) {
    return this.http.post(SOFTWARE_BIKES_LIST_CSV, postData, this.getTextHeader());
  }

  public getUnassignedBikeList() {
    return this.http.get(UNASSIGNEDBIKE_LIST, this.getHeader());
  }

  public openServerErrorDialog(status: number) {
    this.serverErrorDialog = true;
    const data: ErrorDialogData = {
      title: this.languageService.languageJSON.Global_ServerError_Title,
      message: this.languageService.languageJSON.Global_ServerError_Description
    }
    if (status == 403) {
      data.title = this.languageService.languageJSON.Global_ServerError_403_Title;
      data.message = this.languageService.languageJSON.Global_ServerError_403_Description;
    }
    this.matDialog.open(ErrorDialogComponent, { data: { data } }).afterClosed().subscribe(() => {
      this.serverErrorDialog = false;
    });
  }
}
